import { TResult, makeSuccess, makeFailure } from "../../sg-core/utils/Result";
import { ISgApiService } from "../../sg-core/services/ApiService2";

import { IInitialContextResult } from "./contextService";
import { IEndpoint } from "../../sg-core/services/EndpointService";
import IAudit, { INewAudit, IAuditListSearch, IAuditListFilters, TAuditCounters, EAuditStatus } from "../interfaces/IAudit";
import AuditMapper, { newAuditMapper } from "../mappers/AuditMapper";
import { IPagination } from "../interfaces/common/IPagination";
import { ContextMapper } from "../mappers/ContextMapper";
import { IUnparsedContext } from "../interfaces/IAuditoinnitContext";
import { IAuditItem } from "../interfaces/IAuditItem";
import AuditItemMapper from "../mappers/AuditItemMapper";
import { AuditHistoryEventMapper, IAuditHistoryEvent, IAuditHistoryEventDTO, INewAuditHistoryEvent, NewAuditHistoryEventMapper } from "../interfaces/IAuditHistoryEvent";
import { base64ToJson, jsonToBase64 } from "../../sg-core/utils/json.helpers";
import { IAudtiPackage } from "../interfaces/IAuditPackage";
import { extractFormDataIdentifiersDTO, extractIdentifiersDTOFromAudit, TIdentifiersDTO } from "../utils/id.helper";

export interface IAuditService {
    getAudit: (auditGuid: IAudit["guid"]) => Promise<TResult<{ audit: IAudit; context: IUnparsedContext; }>>
    getAuditItem: (auditGuid: IAudit["guid"]) => Promise<TResult<IAuditItem>>

    getAudits: (search?: IAuditListSearch) => Promise<TResult<{ audits: IAuditItem[]; context: IUnparsedContext; }>>
    getMapAudits: (search?: IAuditListFilters) => Promise<TResult<{ audits: IAuditItem[]; context: IUnparsedContext; }>>
    getAuditPage: (pagination: IPagination, search?: IAuditListFilters | undefined) => Promise<TResult<{ totalItems: number; audits: IAuditItem[]; context: IUnparsedContext }>>
    getAuditCounts(search?: IAuditListFilters): Promise<TResult<TAuditCounters>>;

    removeAudit(audit: IAudit): Promise<TResult<IAudit>>;

    completeAudit(audit: IAudit): Promise<TResult<{ audit: IAudit }>>;
    saveAuditDraft(audit: IAudit): Promise<TResult<{ audit: IAudit }>>;
    saveAudit(audit: IAudit): Promise<TResult<{ audit: IAudit }>>;

    createAudit(audit: INewAudit): Promise<TResult<{ audit: IAudit }>>;

    createHistoryEvent(event: INewAuditHistoryEvent): Promise<TResult<{ event: IAuditHistoryEvent }>>;

    saveHistoryData: (jsonData: IAudtiPackage) => Promise<TResult<{ id: string; }>>;
    getHistoryDataAnonym: (id: string) => Promise<TResult<{ data: IAudtiPackage, sid: string }>>;

}

export interface IAuditResult {
    success: true;
    action: "hae_havainto";
    auditointi: {
        id: number;
        guid: string;
        otsikko: string;

        lomake: number;
        alue: string | null | undefined;
        tyo?: number;
        tila: string;
        lomakedata: string;
        metadata: string;

        kirjattu: string | undefined;
        kirjaaja?: number;

        karttasijainti?: string;

        vastuuhenkilo?: number;

        created?: string
        creator: number;

        modified?: string;
        modifier?: number;
        poistettu: "Y" | "N" | "";
        lomakedatahaku?: string
    }
    henkilot: IInitialContextResult["henkilot"];
    lomakkeet: IInitialContextResult["lomakkeet"];
    kategoriat: IInitialContextResult["kategoriat"];
}

export interface IAuditCountsByStatusResult {
    success: true;
    action: "hae_auditointien_maarat_tiloittain";
    auditoinnit: {
        [EAuditStatus.Draft]: { tila: EAuditStatus.Draft; maara: number };
        [EAuditStatus.Finished]: { tila: EAuditStatus.Finished; maara: number };
    };

}
export interface IAuditListResult {
    success: true;
    action: "tallenna_havainto";
    auditoinnit?: any[];
    haku: { tietuemaara: number };
    henkilot: IInitialContextResult["henkilot"];
    lomakkeet: IInitialContextResult["lomakkeet"];
    kategoriat: IInitialContextResult["kategoriat"];
}

export interface ICreateAuditBody {
    success: true;
    action: "tallenna_havainto";
    auditointi: Omit<IAuditResult["auditointi"], "id" | "guid" | "modified" | "modifier" | "kirjattu" | "kirjaaja" | "poistettu">;
}



export const getAuditService = (apiService: ISgApiService, Enpoint: IEndpoint, anonymousSession: boolean): IAuditService => {

    const makeAnonymousAction = (actionName: string) => anonymousSession ? `${actionName}_anonyymi` : actionName;


    const Action: { [K in keyof IAuditService]: string; } = {
        getAudit: "hae_auditointi",
        getAuditItem: "hae_auditointi",
        getAudits: "listaa_auditoinnit",
        getMapAudits: "listaa_kartta_auditoinnit",
        getAuditPage: "listaa_auditoinnit",
        getAuditCounts: "hae_auditointiten_maarat_tiloittain",
        completeAudit: "tallenna_auditointi",
        saveAuditDraft: "tallenna_auditointi",
        saveAudit: "tallenna_auditointi",
        createAudit: "lisaa_auditointi",
        removeAudit: "poista_auditointi",
        createHistoryEvent: "lisaa_tapahtuma",
        saveHistoryData: "tallenna_historiapaketti",
        getHistoryDataAnonym: "hae_historiapaketti_anonyymi",
    }



    const getAudit: IAuditService["getAudit"] = async (auditGuid) => {
        const action = Action.getAudit;
        const reqBody = { auditointi: { guid: auditGuid, } };
        const apiResponse = await apiService.sendAction<{ auditointi: any }>(Enpoint, action, reqBody);

        if (apiResponse.isSuccess === true) {
            const audit = AuditMapper.apiToDomain(apiResponse.value.auditointi);
            return makeSuccess({
                audit,
                context: {}
            });
        } else if (
            process.env.NODE_ENV === "development" &&
            (apiResponse as any).error?.message?.startsWith("tunnisterekisterikentän sisältö puuttuu")
        ) {

            return makeSuccess({
                audit: {
                    missingIdRegister: true, ...AuditMapper.apiToDomain((apiResponse as any).error.data.auditointi)
                },
                context: {}
            });
        }

        return apiResponse;
    };


    const getAuditItem: IAuditService["getAuditItem"] = async (auditGuid) => {
        const action = Action.getAuditItem;
        const reqBody = { auditointi: { guid: auditGuid, } };
        const apiResponse = await apiService.sendAction<{ auditointi: any }>(Enpoint, action, reqBody);

        if (apiResponse.isSuccess === true) {
            const audit = AuditItemMapper.apiToDomain(apiResponse.value.auditointi);
            return makeSuccess(audit);
        }
        return apiResponse;
    };


    const getAudits: IAuditService["getAudits"] = async (search) => {
        const action = Action.getAudits;
        const body = {
            haku: {
                kategoria: search?.category,
                roolilista: search?.roles?.join(","),
                tila: "valmis"
            }
        };
        const apiResponse = await apiService.sendAction<IAuditListResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const auditoinnit = apiResponse.value.auditoinnit || [];
            return makeSuccess({
                audits: auditoinnit.map(AuditItemMapper.apiToDomain),
                context: ContextMapper.apiToDomain(apiResponse.value),
            });
        }

        return apiResponse
    };

    const getMapAudits: IAuditService["getMapAudits"] = async (search) => {
        const action = Action.getMapAudits;
        const body = {
            haku: {

                kategoria: search?.category,
                roolilista: search?.roles?.join(","),
                tila: "valmis",
                //@ts-ignore
                lomake: search?.formType,
                //@ts-ignore
                vastuuhenkilo: search?.responsiblePerson,
                sijainti: search?.region,
                kohde: search?.target,
                alue: search?.area,

            }
        };
        const apiResponse = await apiService.sendAction<IAuditListResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const auditoinnit = apiResponse.value.auditoinnit || [];
            return makeSuccess({
                audits: auditoinnit.map(AuditItemMapper.apiToDomain),
                context: ContextMapper.apiToDomain(apiResponse.value),
            });
        }

        return apiResponse
    };


    /**
     * Fetches a single page of observations according to the given pagination &
     * search filtering oprions
     * @param pagination Object with page & pageSize
     * @param search Filters to use in the search
     */
    const getAuditPage: IAuditService["getAuditPage"] = async (pagination, search) => {
        const action = Action.getAuditPage;

        const body = {
            haku: {

                // Set up pagination
                sivunumero: pagination.page,
                sivupituus: pagination.pageSize,

                // Optional filters
                tila: search?.status,
                teksti: search?.freeText,
                vastuuhenkilo: search?.responsiblePerson,

                sijainti: search?.region,
                kohde: search?.target,
                alue: search?.area,

                lomake: search?.formType,

                roolilista: search?.roles && Array.isArray(search?.roles) ? search?.roles.join(",") : [],
                kirjaaja: search?.creator,
                kategoria: search?.category
            },
        };

        // Fetch the data
        const apiResponse = await apiService.sendAction<IAuditListResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const { auditoinnit, haku } = apiResponse.value;
            // Map the fetched observation list and the data context into domain objects
            const audits = (auditoinnit || []).map(AuditItemMapper.apiToDomain);
            const context = ContextMapper.apiToDomain(apiResponse.value);
            const totalItems = haku.tietuemaara;
            return makeSuccess({
                totalItems,
                audits,
                context,
            });
        }

        return makeFailure(apiResponse.error);
    };


    const getAuditCounts: IAuditService["getAuditCounts"] = async (search) => {
        const action = Action.getAuditCounts;
        const body = {
            haku: {
                roolilista: search?.roles?.join(","),
                kirjaaja: search?.creator,
                // kasittelija: search?.handler,
                sijainti: search?.region,
                kohde: search?.target,
                alue: search?.area,

            }
        }
        const apiResponse = await apiService.sendAction<IAuditCountsByStatusResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const { auditoinnit } = apiResponse.value
            return makeSuccess({
                [EAuditStatus.Finished]: auditoinnit[EAuditStatus.Finished].maara,
                [EAuditStatus.Draft]: auditoinnit[EAuditStatus.Draft].maara,
                [EAuditStatus.Unknown]: 0
            });
        }

        return apiResponse
    };


    const makeAuditBody = (audit: IAudit, mapper: (audit: IAudit) => any) => {
        if (!audit.formSchema) {
            throw Error("Missing form configurations")
        }

        const body: { auditointi: any, tunnisteet: TIdentifiersDTO | string; } = {
            auditointi: {
                ...mapper(audit),
            },
            tunnisteet: extractIdentifiersDTOFromAudit(audit)
        };

        return body;
    }


    const completeAudit: IAuditService["completeAudit"] = async (audit) => {
        const action = Action.completeAudit;
        const body = makeAuditBody({ ...audit, status: EAuditStatus.Finished }, AuditMapper.domainToApi);
        const apiResponse = await apiService.sendAction<IAuditResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ audit: AuditMapper.apiToDomain(apiResponse.value.auditointi) });
        }
        return apiResponse
    };

    const saveAuditDraft: IAuditService["saveAuditDraft"] = async (audit) => {
        let data = { ...audit };
        data.modifiedAt = new Date();

        const action = Action.saveAuditDraft;
        const body = makeAuditBody({ ...audit, status: EAuditStatus.Draft }, AuditMapper.domainToApi);
        const apiResponse = await apiService.sendAction<IAuditResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ audit: AuditMapper.apiToDomain(apiResponse.value.auditointi) });
        }
        return apiResponse
    };


    const saveAudit: IAuditService["saveAudit"] = async (audit) => {
        const action = Action.saveAudit;
        const body = makeAuditBody(audit, AuditMapper.domainToApi);
        const apiResponse = await apiService.sendAction<IAuditResult>(Enpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess({ audit: AuditMapper.apiToDomain(apiResponse.value.auditointi) });
        }
        return apiResponse
    };



    const createAudit: IAuditService["createAudit"] = async (audit) => {
        const action = Action.createAudit;
        const body = makeAuditBody({ ...audit, status: EAuditStatus.Draft } as any, newAuditMapper.domainToApi);
        const apiResponse = await apiService.sendAction<IAuditResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ audit: AuditMapper.apiToDomain(apiResponse.value.auditointi) });
        }
        return apiResponse
    };


    const removeAudit: IAuditService["removeAudit"] = async (audit) => {
        const action = Action.removeAudit;
        const body = {
            auditointi: {
                id: audit.id,
            }
        }
        const apiResponse = await apiService.sendAction<IAuditResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(AuditMapper.apiToDomain(apiResponse.value.auditointi));
        }
        return apiResponse;
    };



    const createHistoryEvent: IAuditService["createHistoryEvent"] = async (newEvent) => {
        const action = Action.createHistoryEvent
        const body = {
            tapahtuma: NewAuditHistoryEventMapper.domainToApi(newEvent),
            tunnisteet: "n/a"
        }
        const apiResponse = await apiService.sendAction<{ tapahtuma: IAuditHistoryEventDTO }>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ event: AuditHistoryEventMapper.apiToDomain(apiResponse.value.tapahtuma) });
        }
        return apiResponse
    };


    const saveHistoryData: IAuditService["saveHistoryData"] = async (jsonData) => {
        const action = Action.saveHistoryData;
        const body = {
            base64Data: jsonToBase64(jsonData),
            tunnisteet: {
                auditoinnit: [jsonData.audit.id].join(","),
                kohteet: Object.keys(jsonData.data.locations).join(","),
                henkilot: Object.keys(jsonData.data.persons).join(","),
                yritykset: Object.keys(jsonData.data.persons).map((key) => jsonData.data.persons[key].company).filter(((comp) => comp)).join(",")
            }
        };

        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ id: apiResponse.value.id })
        }
        return apiResponse
    }


    const getHistoryDataAnonym: IAuditService["getHistoryDataAnonym"] = async (id) => {
        const action = Action.getHistoryDataAnonym;
        const body = { id };
        const noToken = true;
        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body, noToken);

        if (apiResponse.isSuccess === true) {
            try {
                const data = base64ToJson(apiResponse.value.base64data);
                return makeSuccess({ data, sid: apiResponse.value.sid })
            } catch (err) {
                return makeFailure(err);
            }
        }
        return apiResponse;
    }





    return {
        getAudit,
        getAuditItem,
        getAudits,
        getMapAudits,
        getAuditPage,
        getAuditCounts,

        completeAudit,
        createAudit,
        saveAuditDraft,
        saveAudit,
        removeAudit,

        createHistoryEvent,
        saveHistoryData,
        getHistoryDataAnonym
    };
};
